import React, { useState } from 'react';
import { Box, List, ListItem, ListItemText, ListSubheader, Theme, useTheme } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Typography, FormControlLabel, Switch, TextField, Button, IconButton } from '@mui/material';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as NotificationActions from 'actions/NotificationActions';
import { getDefaultPaymentOptions } from 'services/utils';
import { useTranslation } from 'services/localization/useTranslation';
import { useHasFeature } from 'hooks';
import ContainerBox from 'components/ContainerBox';
import { RiDeleteBinLine, RiPencilLine } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { api } from 'common/frontend/api';
import { getPaymentMethodTranslation } from 'services/translations';

const POS = () => {
	const classes = useStyles();
	const { t } = useTranslation();
	const theme = useTheme();
	const dispatch = useDispatch();
	const { hasFeature } = useHasFeature();
	const customPosPaymentMethods = useSelector(ShopSelectors.customPosPaymentMethods);
	const useAsPos = useSelector(ShopSelectors.getUseAsPos);
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const [editingMethodIndex, setEditingMethodIndex] = useState<number>(-1);
	const [editedPaymentMethod, setEditedPaymentMethod] = useState<null | string>(null);
	const [addingNewPaymentMethod, setAddingNewPaymentMethod] = useState<boolean>(false);
	const customPaymentMethods = [
		...customPosPaymentMethods.map((method, index) =>
			index === editingMethodIndex ? editedPaymentMethod! : method,
		),
		...(addingNewPaymentMethod && editedPaymentMethod !== null ? [editedPaymentMethod] : []),
	];
	const defaultPaymentMethods = getDefaultPaymentOptions();
	const isPosFeatureEnabled = hasFeature('POS');

	const handleUseAsPosChange = async (
		event: React.ChangeEvent<HTMLInputElement>,
		checked: boolean,
	) => {
		try {
			await api().shops.doc(shopId).update({
				useAsPos: checked,
			});
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}
	};

	const addCustomMethod = () => {
		setEditedPaymentMethod('');
		setEditingMethodIndex(customPaymentMethods.length);
		setAddingNewPaymentMethod(true);
	};

	const handleCustomPaymentMethodNameChange = (
		event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>,
		index: number,
	) => {
		setEditedPaymentMethod(event.target.value);
	};

	const saveCustomPaymentMethod = async () => {
		setEditedPaymentMethod(null);
		setEditingMethodIndex(-1);
		setAddingNewPaymentMethod(false);
		try {
			await api().shops.doc(shopId).update({
				customPaymentMethods,
			});
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}
	};

	const deleteCustomPaymentMethod = async (index: number) => {
		const customPaymentMethods = customPosPaymentMethods.filter((method, i) =>
			index === i ? false : true,
		);
		try {
			await api().shops.doc(shopId).update({
				customPaymentMethods,
			});
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}
		setEditedPaymentMethod(null);
		setEditingMethodIndex(-1);
	};

	if (!isPosFeatureEnabled) return null;
	return (
		<ContainerBox>
			<Box mb={3}>
				<Typography variant="h5">{t('shop.rentlePos', 'Rentle POS')}</Typography>
				<Typography variant="body2" color="textSecondary">
					{t(
						'shop.rentlePosInfo2',
						'Use Rentle as point of sale system. Mark orders as paid with different payment methods, print payment receipts and get accounting reports for store payments',
					)}
				</Typography>
			</Box>
			<FormControlLabel
				control={
					<Switch
						checked={!!useAsPos}
						onChange={handleUseAsPosChange}
						value={!!useAsPos}
						color="primary"
					/>
				}
				label={t('shop.useAsPos', 'Use Rentle POS')}
			/>
			{useAsPos ? (
				<>
					<List>
						<ListItem sx={{ paddingLeft: 0 }}>
							<ListSubheader
								inset
								classes={{
									root: classes.listItemText,
									inset: classes.listItemTextInset,
								}}
							>
								{t('shop.paymentMethods', 'Payment methods')}
							</ListSubheader>
						</ListItem>

						{defaultPaymentMethods.map((method, index) => (
							<ListItem divider className={classes.listItem}>
								<ListItemText
									classes={{ root: classes.listItemText }}
									inset
									sx={{
										paddingLeft: 0,
									}}
									primary={
										<Typography variant="body1">
											{getPaymentMethodTranslation(method, t)}
										</Typography>
									}
								/>

								<ListItemText
									classes={{ root: classes.listItemButtons, inset: classes.listItemTextInset }}
									inset
									sx={{ paddingLeft: 0 }}
									primary={
										<Typography
											color="textSecondary"
											align="left"
											sx={{ paddingRight: theme.spacing(1.5) }}
										>
											{t('shop.default', 'Default')}
										</Typography>
									}
								/>
							</ListItem>
						))}
						{!!customPaymentMethods.length &&
							customPaymentMethods.map((method, index) => (
								<ListItem divider className={classes.listItem}>
									<ListItemText
										classes={{ root: classes.listItemText }}
										inset
										sx={{
											paddingLeft: 0,
										}}
										primary={
											editingMethodIndex === index ? (
												<TextField
													id={`customMethodName${index}`}
													placeholder={t('shop.paymentMethodName', `Payment method's name`)}
													name="name"
													value={method || ''}
													onChange={(e) => handleCustomPaymentMethodNameChange(e, index)}
													className={classes.textField}
													margin="none"
													type="text"
												/>
											) : (
												<Typography variant="body1">{method}</Typography>
											)
										}
									/>

									<ListItemText
										classes={{
											root: classes.listItemButtons,
											inset: classes.listItemTextInset,
										}}
										inset
										primary={
											editingMethodIndex === index ? (
												<>
													<Button
														sx={{ margin: theme.spacing(0, 2) }}
														variant="outlined"
														color="primary"
														size="small"
														onClick={() => {
															setEditingMethodIndex(-1);
															setEditedPaymentMethod(null);
															setAddingNewPaymentMethod(false);
														}}
													>
														{t('shop.cancel', 'Cancel')}
													</Button>

													<Button
														variant="contained"
														color="primary"
														size="small"
														onClick={() => saveCustomPaymentMethod()}
													>
														{t('common:actions.save')}
													</Button>
												</>
											) : (
												<>
													<IconButton
														onClick={() => {
															setEditingMethodIndex(index);
															setEditedPaymentMethod(method);
															setAddingNewPaymentMethod(false);
														}}
														color="primary"
													>
														<RiPencilLine size={24} />
													</IconButton>
													<IconButton
														color="primary"
														onClick={() => deleteCustomPaymentMethod(index)}
													>
														<RiDeleteBinLine size={24} />
													</IconButton>
												</>
											)
										}
									/>
								</ListItem>
							))}
					</List>
					{!addingNewPaymentMethod && (
						<Button
							className={classes.saveBtn}
							variant="outlined"
							color="primary"
							onClick={() => addCustomMethod()}
						>
							<Typography variant="button" color="inherit">
								{t('shop.addPaymentMethod', 'Add payment method')}
							</Typography>
						</Button>
					)}
				</>
			) : null}
		</ContainerBox>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		listItem: {
			padding: theme.spacing(2, 2, 2, 0),
		},
		listItemText: {
			minWidth: '20%',
			paddingRight: '100px',
			[theme.breakpoints.down('sm')]: {
				paddingRight: 0,
			},
			'&:first-child': {
				minWidth: '40%',
				paddingRight: '50px',
			},
		},
		listItemTextInset: {
			paddingLeft: 0,
		},
		listItemButtons: {
			paddingRight: 0,
			justifyContent: 'flex-end',
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			[theme.breakpoints.down('sm')]: {
				paddingRight: 0,
			},
		},
		saveBtn: {
			display: 'block',
			marginTop: theme.spacing(1),
		},
		textField: {
			maxWidth: 380,
			width: '100%',
		},
	}),
);

export default POS;
